import './PaintersDelta.scss';

import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';
import { googleReviews, initials } from '../data/googleReviews';
import { ArrowRight, Award, Check, Clock, Phone, Shield, Sparkle } from '../components/Icons';
import p1 from '../assests/16.jpeg';
import p2 from '../assests/13.jpeg';
import p3 from '../assests/1.jpeg';
import p4 from '../assests/5.jpeg';
import p5 from '../assests/9.jpeg';
import p6 from '../assests/14.jpeg';

const areas = [
  {
    name: 'Ladner',
    text: 'Our home base. Heritage homes around the village, newer builds and everything in between.',
    places: ['Ladner Village', 'Delta Manor', 'Hawthorne', 'Holly', 'Port Guichon', 'Neilson Grove', 'East Delta', 'Westham Island'],
  },
  {
    name: 'Tsawwassen',
    text: 'Coastal exteriors take a beating from salt air and wind. We prep and coat them to last.',
    places: ['Beach Grove', 'Boundary Bay', 'Cliff Drive', 'English Bluff', 'Pebble Hill', 'Tsawwassen Central', 'Tsawwassen East', 'Tsawwassen Springs'],
  },
  {
    name: 'North Delta',
    text: 'Family homes, townhouse complexes and strata buildings from Nordel to Sunshine Hills.',
    places: ['Nordel', 'Sunshine Hills', 'Scottsdale', 'Annieville', 'Sunbury', 'Kennedy', 'Royal York', 'Delsom'],
  },
];

const services = [
  'Interior painting',
  'Exterior painting',
  'Kitchen cabinet painting',
  'Strata and townhouse painting',
  'Commercial and office painting',
  'Warehouse and industrial painting',
  'Deck and fence staining',
  'Power washing and prep',
];

const steps = [
  { title: 'Free on-site quote', text: 'Our appraiser visits your Delta home, walks the space with you and leaves a clear written quote. No pressure, no surprises.' },
  { title: 'Prep and protect', text: 'We cover floors and furniture, wash and sand surfaces, fill and repair, and prime where needed so the finish lasts.' },
  { title: 'Precision painting', text: 'Premium products, clean lines and even coats, on the schedule we agreed to.' },
  { title: 'Walkthrough and guarantee', text: 'We inspect every surface with you, clean up completely and back the work with a 3-year guarantee.' },
];

const reasons = [
  { icon: <Shield />, title: '$5M liability insurance', text: 'Fully licensed and insured on every job, so you never carry the risk.' },
  { icon: <Award />, title: 'BBB A+ accredited', text: 'Award-winning service verified by the Better Business Bureau.' },
  { icon: <Sparkle />, title: '3-year guarantee', text: 'If the finish does not hold up, we come back and make it right. No fine print.' },
  { icon: <Clock />, title: 'On time and tidy', text: 'We show up when we say we will, protect your space and clean up completely.' },
];

// Google reviews from customers in Tsawwassen and South Delta.
const deltaReviewIds: Record<string, string> = {
  'stan-baldwin': 'Tsawwassen',
  'beachgrove-mom': 'Beach Grove, Tsawwassen',
  'corilee-cotter': 'South Delta',
};
const reviews = googleReviews
  .filter((r) => r.id in deltaReviewIds)
  .map((r) => ({ text: r.text, name: r.name, where: deltaReviewIds[r.id] + ' · Google review' }));

const photos = [p1, p2, p3, p4, p5, p6];

const faqs = [
  { q: 'Do you offer free estimates in Delta?', a: 'Yes. We come to you anywhere in Ladner, Tsawwassen or North Delta, walk the space with you and leave a written quote with no obligation.' },
  { q: 'How long does a typical painting project take?', a: 'A single room is usually done in a day or two, a full interior in about a week, and most exteriors in one to two weeks depending on size and weather. We confirm the schedule in your quote and stick to it.' },
  { q: 'Are you licensed and insured?', a: 'We are fully licensed and carry $5 million in liability insurance on every job. We are also BBB A+ accredited.' },
  { q: 'Is your work guaranteed?', a: 'Every painting project comes with a 3-year, 100% satisfaction guarantee. If the finish does not hold up, we come back and make it right.' },
  { q: 'Do I need to buy the paint myself?', a: 'No. We supply premium products from trusted brands and include patient colour advice until you are sure. If you already have a colour in mind, we match it.' },
  { q: 'Do you paint strata, commercial and warehouse buildings in Delta?', a: 'Yes. We scale our crew and schedule to buildings of every size, from a single townhouse to a full complex, office or warehouse.' },
];

const d = (ms: number) => ({ '--delay': `${ms}ms` } as React.CSSProperties);

export function PaintersDelta() {
  useSeo({
    title: 'Painters in Delta, BC | Ladner, Tsawwassen & North Delta | Ivy League Painting',
    description:
      'Local Delta painters for interiors, exteriors, kitchen cabinets, strata and commercial work in Ladner, Tsawwassen and North Delta. BBB A+, $5M insured, 3-year guarantee. Free on-site quotes.',
    canonical: 'https://www.ivyleaguepainting.com/painters-delta',
  });

  return (
    <main className="dp">
      <section className="section dp__hero on-dark">
        <div className="dp__glow" aria-hidden="true" />
        <div className="container">
          <span className="eyebrow reveal">Delta, BC</span>
          <h1 className="reveal" style={d(80)}>
            Painters in Delta, BC.
          </h1>
          <p className="dp__lede reveal" style={d(160)}>
            Ivy League Painting is based in Ladner and has painted homes, strata buildings and businesses across
            Delta for over 10 years. Interior, exterior, cabinets, strata and commercial, all with a written quote,
            $5M insurance and a 3-year guarantee.
          </p>
          <div className="dp__cta reveal" style={d(240)}>
            <Link className="btn btn--blue" to="/contact">
              Get a free quote
            </Link>
            <a className="btn btn--glass" href="tel:+16043655225">
              <Phone /> (604) 365-5225
            </a>
          </div>
        </div>
      </section>

      <section className="section section--grey">
        <div className="container">
          <div className="head">
            <span className="eyebrow reveal">Where we work</span>
            <h2 className="reveal" style={d(80)}>
              Ladner, Tsawwassen and North Delta
            </h2>
          </div>
          <ul className="dp__areas">
            {areas.map((a, i) => (
              <li key={a.name} className="card reveal" style={d(i * 90)}>
                <h3>{a.name}</h3>
                <p>{a.text}</p>
                <ul className="dp__places">
                  {a.places.map((pl) => (
                    <li key={pl}>{pl}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container split">
          <div>
            <span className="eyebrow reveal">What we paint</span>
            <h2 className="reveal" style={d(80)}>
              Every surface in Delta
            </h2>
            <p className="lede reveal" style={d(160)}>
              From a single feature wall to a full strata exterior, the same care, precision and clean-up on every
              job.
            </p>
          </div>
          <ul className="dp__services">
            {services.map((s, i) => (
              <li key={s} className="reveal" style={d(i * 50)}>
                <Check /> {s}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section--grey">
        <div className="container">
          <div className="head head--row">
            <div>
              <span className="eyebrow reveal">How a quote works</span>
              <h2 className="reveal" style={d(80)}>
                Four steps. Zero surprises.
              </h2>
            </div>
            <p className="lede reveal" style={d(160)}>
              Most Delta rooms are done in a day or two, full interiors in about a week, and exteriors in one to two
              weeks, weather permitting. Your quote confirms the dates.
            </p>
          </div>
          <ol className="dp__steps">
            {steps.map((s, i) => (
              <li key={s.title} className="card reveal" style={d(i * 90)}>
                <span className="dp__num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="head head--center">
            <span className="eyebrow reveal">Why Ivy League</span>
            <h2 className="reveal" style={d(80)}>
              Why Delta homeowners choose us
            </h2>
          </div>
          <ul className="dp__reasons">
            {reasons.map((r, i) => (
              <li key={r.title} className="card reveal" style={d(i * 80)}>
                <span className="icon-chip">{r.icon}</span>
                <h3>{r.title}</h3>
                <p>{r.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section--grey">
        <div className="container">
          <div className="head">
            <span className="eyebrow reveal">Reviews</span>
            <h2 className="reveal" style={d(80)}>
              What Delta clients say
            </h2>
          </div>
          <ul className="dp__reviews">
            {reviews.map((r, i) => (
              <li key={r.name} className="card reveal" style={d(i * 90)}>
                <p>“{r.text}”</p>
                <footer>
                  <span className="dp__avatar" aria-hidden="true">
                    {initials(r.name)}
                  </span>
                  <div>
                    <strong>{r.name}</strong>
                    <span>{r.where}</span>
                  </div>
                </footer>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="head head--row">
            <div>
              <span className="eyebrow reveal">Recent work</span>
              <h2 className="reveal" style={d(80)}>
                Delta homes, transformed
              </h2>
            </div>
            <Link className="btn btn--ghost reveal" to="/work" style={d(160)}>
              See the full gallery <ArrowRight />
            </Link>
          </div>
          <ul className="dp__photos">
            {photos.map((src, i) => (
              <li key={src} className="reveal" style={d(i * 60)}>
                <img src={src} alt={`Painting project in Delta, BC (${i + 1})`} loading="lazy" />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section section--grey">
        <div className="container dp__faq">
          <div className="head head--center">
            <span className="eyebrow reveal">Questions</span>
            <h2 className="reveal" style={d(80)}>
              Painting in Delta, answered
            </h2>
          </div>
          {faqs.map((f, i) => (
            <details key={f.q} className="card reveal" style={d(i * 60)} open={i === 0}>
              <summary>
                <h3>{f.q}</h3>
              </summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="section dp__band on-dark">
        <div className="dp__glow" aria-hidden="true" />
        <div className="container">
          <h2 className="reveal">
            Ready for a fresh coat in Delta?
          </h2>
          <p className="lede reveal" style={d(80)}>
            Tell us about your project and we'll follow up within one business day with a free, written quote.
          </p>
          <div className="dp__cta reveal" style={d(160)}>
            <Link className="btn btn--blue" to="/contact">
              Request a quote
            </Link>
            <a className="btn btn--white" href="tel:+16043655225">
              <Phone /> (604) 365-5225
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
